import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { Observable, Subscription } from 'rxjs';
import { APP_ICONS } from 'src/app/icons/icon-provider';
import { ApiService } from 'src/app/services/api.service';
import { AppSettings } from 'src/app/services/AppSettings';
import { JobService } from 'src/app/services/job.service';
import { SlidePanelTab } from 'src/app/services/slide-panel.service';
import { UtilService } from 'src/app/services/util.service';
import { PricePlanService } from 'src/app/components/Price-subscritions/priceplanService';
import { MessageWidgetComponent } from 'src/app/widget/message-widget/message-widget.component';
import { CpAiInsightsComponent } from '../shared/cp-ai-insights/cp-ai-insights.component';
import { CpHeaderComponent } from '../shared/cp-header/cp-header.component';
import { CpJdBodyComponent } from '../shared/cp-jd-body/cp-jd-body.component';
import { CpRecStripComponent } from '../shared/cp-rec-strip/cp-rec-strip.component';
import { ConnectionTemplateComponent } from 'src/app/templates/connection-template/connection-template.component';
import { CommonSkeletonComponent } from 'src/app/skeleton/common-skeleton/common-skeleton.component';

/**
 * V3 job profile slider — mirrors the candidate slider for the job
 * direction (candidate viewing a job from Applied / Marketplace Jobs).
 * No mock exists for this surface; structure follows the candidate slider
 * so users get a consistent interaction on both sides.
 *
 * Same lazy-tab pattern as candidate slider — AI fetch + Chat WebSocket
 * only fire when their tab is active.
 */
@Component({
  selector: 'app-job-profile-slider',
  standalone: true,
  imports: [
    CommonModule,
    NgIconComponent,
    CpHeaderComponent,
    CpRecStripComponent,
    CpAiInsightsComponent,
    CpJdBodyComponent,
    MessageWidgetComponent,
    ConnectionTemplateComponent,
    CommonSkeletonComponent,
  ],
  providers: [provideIcons(APP_ICONS)],
  templateUrl: './job-profile-slider.component.html'
})
export class JobProfileSliderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() jobSummary: any = null;
  @Input() candidateId: string | undefined;
  /** Candidate context (the candidate whose Applied tab this slider was
   *  opened from) — source of the "Submitted for" rate. Null when opened
   *  without a candidate (e.g. Job Center). */
  @Input() candidateData: any = null;
  @Input() initialTab: SlidePanelTab | undefined;
  @Input() pageFrom: string | undefined;
  @Input() hideAiInsights = false;

  @Output() closeRequested = new EventEmitter<void>();

  fullJob: any = null;
  loadingJob = false;

  activeTab: SlidePanelTab = 'profile';

  // Chat state — same pattern as candidate-profile-slider, which itself
  // mirrors candidate-card-table-view.
  messagemodelflag = false;
  messageData: any = null;
  seletedChatItem: any = null;

  readonly url = AppSettings.ServerUrl;
  private readonly viewerId = localStorage.getItem('userId');

  constructor(
    private jobService: JobService,
    private apiService: ApiService,
    private util: UtilService,
    private planService: PricePlanService,
  ) { }

  ngOnInit(): void {
    this.activeTab = this.initialTab || 'profile';
    this.loadFullJob();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jobSummary'] && !changes['jobSummary'].firstChange) {
      this.fullJob = null;
      this.activeTab = this.initialTab || 'profile';
      this.messagemodelflag = false;
      this.loadFullJob();
    }
  }

  ngOnDestroy(): void {
    this.jobSub?.unsubscribe();
  }

  /** In-flight findByJobId. The drawer is reused (not re-created) across
   *  openings, so clicking row after row in quick succession used to leave
   *  several fetches racing and whichever settled last won — the drawer
   *  could end up hydrated with a job the user had already clicked past.
   *  Cancel the previous one before issuing the next. */
  private jobSub?: Subscription;

  private loadFullJob(): void {
    const id = this.jobSummary?.jobId;
    if (!id) return;
    this.jobSub?.unsubscribe();
    this.loadingJob = true;
    this.jobSub = this.jobService.findByJobId(id).subscribe({
      next: (data: any) => { this.fullJob = data || null; this.loadingJob = false; },
      error: () => { this.loadingJob = false; }
    });
  }

  /** Merged job — summary fields persist if the detailed fetch is missing
   *  them. fullJob overrides summary on field overlap.
   *
   *  Memoised: returning a fresh spread on every change-detection tick
   *  would discard local mutations made by child components — e.g.
   *  `<app-connection-template>` flips `values.connectionStatus` to
   *  'CONNECTED' on accept and relies on that mutation surviving until
   *  the API refresh. Without memoisation the Posted-by strip's
   *  connection state reverts on the next CD pass. */
  private _mergedJob: any = null;
  private _mergedSummaryRef: any = null;
  private _mergedFullRef: any = null;
  get job(): any {
    if (this._mergedSummaryRef !== this.jobSummary
        || this._mergedFullRef !== this.fullJob) {
      this._mergedJob = { ...(this.jobSummary ?? {}), ...(this.fullJob ?? {}) };
      this._mergedSummaryRef = this.jobSummary;
      this._mergedFullRef = this.fullJob;
    }
    return this._mergedJob;
  }

  selectTab(tab: SlidePanelTab): void { this.activeTab = tab; }

  /** Chat tab — mirrors candidate-profile-slider.openChatTab(): swap the
   *  pane immediately (skeleton shows), run the gated chat flow once, and
   *  never re-run it on tab re-clicks while a conversation is open. */
  openChatTab(): void {
    // Own job with no thread → chat is a disabled placeholder; nothing to
    // open (defense-in-depth; the tab/button are also [disabled]).
    if (this.chatDisabled) { return; }
    // Widget already loaded → just reveal the Chat tab, no re-check.
    if (this.messagemodelflag) {
      this.activeTab = 'chat';
      return;
    }
    // Fresh open — do NOT switch to the Chat tab yet. The gated flow only
    // activates the tab once chat actually opens (see openMessage). If the
    // interaction/plan is expired, openMessagecheckInteraction shows the
    // limit popup and never calls openMessage, so we stay on the current tab
    // instead of stranding the user on an empty skeleton pane.
    this.sendMessage(this.job);
  }

  /** Own job with no existing chat thread → chat is a disabled placeholder.
   *  Owners never initiate a chat (they only respond to threads others
   *  started), so with no history there is nothing to open. Mirrors the card's
   *  owner + no-history state; gates the header Chat action + Chat tab. */
  get chatDisabled(): boolean {
    return this.isOwnJobCenterJob && (this.job?.chatHistoryCount || 0) === 0;
  }

  onMatchRingClick(): void { this.selectTab('ai'); }
  onHeaderAction(key: string): void {
    if (key === 'chat') {
      // Lands on the Chat TAB (embedded widget), same as the candidate
      // slider's header Chat action.
      this.openChatTab();
    } else if (key === 'jd') {
      // 'resume' tab slot is reused for Full JD on the job side.
      this.selectTab('resume');
    } else if (key === 'share') {
      this.onShare();
    }
    // apply / save — placeholders; not wired
  }

  /** Share the job via a login-bridge URL — mirrors the SSR pattern
   *  (job-listing.component.ts:25-33 `getJobParams`). Recipient hits
   *  /auth/login; login.component's `absolutePath=true&data=…` branch
   *  rebuilds the destination URL after login. AfterLoginGuard auto-bounces
   *  logged-in recipients past login straight to /newjobs?jobId=…&tabName=…,
   *  so the same URL works for both authed and unauthed viewers.
   *  Web Share API → falls back to clipboard. */
  onShare(): void {
    const jobId = this.job?.jobId;
    if (!jobId) return;
    const data = {
      jobId,
      tabName: 'job-details',
      From: 'JOBS_FROM_CONNECTION'
    };
    // Hash routing (environment.useHash = true) needs `/#/` before /auth/login.
    const url = `${window.location.origin}/#/auth/login`
              + `?redirectTo=/newjobs`
              + `&absolutePath=true`
              + `&data=${encodeURIComponent(JSON.stringify(data))}`;
    const title = this.job?.jobTitle || 'Job';
    const nav: any = window.navigator;
    if (nav?.share) {
      nav.share({ title, url }).catch(() => { /* user cancelled */ });
    } else if (nav?.clipboard) {
      nav.clipboard.writeText(url);
    }
  }

  // ── Chat flow (ported from candidate-card-table-view; job variant) ──

  async sendMessage(item: any): Promise<void> {
    await this.openMessagecheckInteraction(item);
  }

  checkConnectionStatus(values: any): Observable<any> {
    const apiURL = 'user/checkConnectionStatus';
    const params = new HttpParams()
      .set('userId', this.viewerId || '')
      .set('connnectorId', values?.jobPostedBy || '');
    return this.apiService.query(`${apiURL}?${params.toString()}`);
  }

  async openMessagecheckInteraction(seletedItem: any): Promise<void> {
    // Universal first-interaction: skip the ACTIVE_INTERACTIONS gate when this
    // pair's interaction was already consumed (existing chat / prior apply —
    // backend `alreadyConsumed`). Fails safe to gating.
    if (await this.planService.isInteractionAlreadyConsumed(seletedItem?.jobId, 'JOB')) {
      this.chatuser(seletedItem);
      return;
    }
    try {
      this.util.startLoader();
      const userData: any = await this.planService.UPDATE_USERDETAILS('ACTIVE_INTERACTIONS');
      const { userDetail, isExpired, membershipType } = userData || {};
      let availablePoints = null, actualPoints = null, utilizedPoints = null, promotional = null;

      if (userDetail?.userPlanAndBenefits?.benefitsUsages) {
        userDetail.userPlanAndBenefits.benefitsUsages.forEach((ele: any) => {
          if (ele.benefitKey === 'ACTIVE_INTERACTIONS') {
            availablePoints = ele.available;
            actualPoints = ele.actual;
            utilizedPoints = ele.utilized;
            promotional = ele.promotional;
          }
        });
      } else {
        this.util.stopLoader();
        this.chatuser(seletedItem);
        return;
      }

      if (isExpired) {
        const popupType: 'PAY' | 'LIMIT' | 'UPGRADE' =
          ['Starter', 'Growth', 'ScaleX'].includes(membershipType) ? 'LIMIT' : 'PAY';
        this.planService.expiredPopup('ACTIVE_INTERACTIONS', popupType, null, null, null, null,
          availablePoints, actualPoints, utilizedPoints, promotional);
      } else {
        this.util.stopLoader();
        this.chatuser(seletedItem);
      }
    } catch (error) {
      console.error('Error checking interaction:', error);
    } finally {
      setTimeout(() => this.util.stopLoader(), 1500);
    }
  }

  chatuser(seletedItem: any): void {
    if (seletedItem?.jobPostedBy == this.viewerId) {
      this.openMessage(seletedItem);
      return;
    }
    this.checkConnectionStatus(seletedItem).subscribe({
      next: () => {
        if (seletedItem?.isFeatured === true) {
          this.openMessage(seletedItem);
        } else if ((seletedItem?.isFeatured === false || seletedItem?.isFeatured === undefined)
                   && seletedItem?.jobPostedBy != this.viewerId) {
          this.openMessage(seletedItem);
        }
      },
      error: (err: any) => {
        console.error('Error checking connection status:', err);
        this.openMessage(seletedItem);
      }
    });
  }

  openMessage(seletedItem: any): void {
    // Chat is confirmed to open (interaction/plan check passed) — activate the
    // Chat tab now. Deferred to here so an expired-plan popup leaves the user
    // on their current tab instead of an empty skeleton pane.
    this.activeTab = 'chat';
    this.messagemodelflag = false;
    seletedItem.owner = seletedItem?.jobPostedBy == this.viewerId;

    setTimeout(() => {
      seletedItem.userId = seletedItem.jobPostedBy;
      seletedItem.groupType = 'JOB';
      seletedItem.messageType = 'JOB';
      seletedItem.id = seletedItem.jobId;

      this.seletedChatItem = seletedItem;
      this.messageData = seletedItem;
      this.messagemodelflag = true;
    }, 1000);
  }

  closeMessage(_event?: any): void {
    this.messagemodelflag = false;
    // Widget lives inside the Chat tab pane — closing it would leave an
    // empty pane, so bounce back to Job Details.
    if (this.activeTab === 'chat') {
      this.activeTab = 'profile';
    }
  }
  onClose(): void { this.closeRequested.emit(); }

  // ── View helpers ──────────────────────────────────────────────────────

  get jobTitle(): string { return this.job?.jobTitle || 'Job'; }
  // First Unicode letter of a string ('' if none) — ".Net" → "N", "+" → "".
  private firstLetter(s: string): string {
    const m = (s || '').match(/\p{L}/u);
    return m ? m[0] : '';
  }
  // Letters only, so ".Net + XAMARIN + AZURE Developer" reads "NX", not ".+" —
  // same rule as the right-rail job tiles in common-profile-info.
  get jobInitials(): string {
    const words = this.jobTitle.trim().split(/\s+/)
      .filter(w => this.firstLetter(w) !== '');
    const i = words.slice(0, 2).map(w => this.firstLetter(w)).join('');
    return (i || 'J').toUpperCase();
  }
  get jobLogo(): string | null {
    // businessLogo deliberately not read: jobs/filter now returns it but
    // jobs/findJobById does not, so the header flashed logo → initials.
    const photo = this.job?.clientLogo;
    if (!photo) return null;
    return photo.startsWith('http') ? photo : (this.url + 'download/' + photo);
  }
  get jobLocation(): string {
    const j = this.job;
    return [j?.city, j?.state, j?.country].filter(Boolean).join(', ');
  }
  get jobRefId(): string {
    return this.job?.jobReferenceId || this.job?.jobId || '';
  }
  get titleBadge(): { label: string; tone: 'success' | 'warn' | 'neutral' | 'danger'; glyph?: string; icon?: string } | null {
    // Icons mirror the candidate slider pill: bolt for the live state, clock
    // for the paused one, none for terminal states (Draft/Filled/Closed).
    const s = (this.job?.status || '').toString().toUpperCase();
    // Same ⚡ emoji as the candidate slider's Available badge (user-requested
    // parity between the two sliders' success badges).
    if (s === 'ACTIVE') return { label: 'Active', tone: 'success', glyph: '⚡', icon: 'tablerBolt' };
    if (s === 'ON HOLD') return { label: 'On Hold', tone: 'warn', icon: 'tablerClock' };
    if (s === 'DRAFTED') return { label: 'Draft', tone: 'neutral' };
    if (s === 'FILLED' || s === 'CLOSED') return { label: s === 'FILLED' ? 'Filled' : 'Closed', tone: 'danger' };
    return null;
  }

  /** Remaining upgrade (featured) days for the job, or null when not upgraded
   *  / expired. Mirrors job-profile-detail.component.ts:172 so the slider and
   *  the detail page compute the countdown identically. */
  private getActiveUrgencyDays(job: any): number | null {
    if (!job?.isFeatured) return null;
    const now = new Date();
    if (job?.featuredConsumptions?.length > 0) {
      const lastConsumption = job.featuredConsumptions[job.featuredConsumptions.length - 1];
      if (lastConsumption.toDate) {
        const diffMs = new Date(lastConsumption.toDate).getTime() - now.getTime();
        if (diffMs <= 0) return null;
        const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return days > 0 ? days : 1;
      }
      return lastConsumption.dayCount || 1;
    } else if (job?.featuredUpdatedOn) {
      const urgencyEndDate = new Date(new Date(job.featuredUpdatedOn).getTime() + (2 * 24 * 60 * 60 * 1000));
      const days = Math.ceil((urgencyEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return days > 0 ? days : 1;
    }
    return 1;
  }

  /** Upgrade badge for the sticky header — "Urgent Hire" + "Urgency ends in
   *  X days" countdown, matching the job-detail page. Null when not upgraded. */
  get upgradeBadge(): { label: string; countdown?: string } | null {
    const days = this.getActiveUrgencyDays(this.job);
    if (days == null) return null;
    return { label: 'Urgent Hire', countdown: `Urgency ends in ${days} day${days === 1 ? '' : 's'}` };
  }
  get headerMeta(): Array<{ icon: string; text: string }> {
    const j = this.job;
    const items: Array<{ icon: string; text: string }> = [];
    const loc = this.jobLocation;
    if (loc) items.push({ icon: 'tablerMapPin', text: loc });
    if (j?.workMode) items.push({ icon: 'tablerBriefcase', text: j.workMode });
    // durationType (Months/Years) is a separate field — append it so the
    // header meta reads "6 Months" instead of just "6".
    if (j?.duration) {
      const durationText = j?.durationType ? `${j.duration} ${j.durationType}` : `${j.duration}`;
      items.push({ icon: 'tablerClock', text: durationText });
    }
    // Email row — masked as the wireframe's "[email protected]"
    // placeholder. Gated on any job-poster identifier so we don't show
    // a contact line for jobs with no associated user. Same pattern as
    // the candidate slider (candidate-profile-slider.component.ts:332):
    // text-only, no `href`, so the cp-header anchor stays non-clickable.
    const hasPoster = j?.jobPostedBy
      || j?.jobPostedByEmail
      || j?.user?.username
      || j?.user?.candidateEmailId
      || j?.email;
    if (hasPoster) {
      items.push({ icon: 'tablerMail', text: 'email protected' });
    }
    return items;
  }
  get matchScore(): number | null {
    // Mirror the candidate slider's fallback chain (matchingScore →
    // score) so the cp-header ring renders for any surface that
    // populates either field on the job payload.
    const raw = this.jobSummary?.matchingScore
      ?? this.job?.matchingScore
      ?? this.jobSummary?.score
      ?? this.job?.score;
    if (raw == null) return null;
    const n = Number(raw);
    return isNaN(n) ? null : Math.round(n);
  }
  get headerActions() {
    // Mirror the candidate slider's tone split: only the first action is
    // primary purple, secondary actions are outline. Adds Share to match
    // the candidate slider's three-button row.
    const actions: Array<{ key: string; label: string; icon: string; tone: 'primary' | 'outline'; disabled?: boolean }> = [
      { key: 'jd',    label: 'View Full JD', icon: 'tablerFileText', tone: 'primary' },
    ];
    // Chat about the job does not require a peer connection — the
    // connected-status gate only applies to the rec-strip's personal chat
    // with the poster (which routes to the message page). Always offer the
    // header Chat action here.
    actions.push({ key: 'chat', label: 'Chat', icon: 'tablerMessage', tone: 'outline', disabled: this.chatDisabled });
    actions.push({ key: 'share', label: 'Share', icon: 'tablerShare', tone: 'outline' });
    return actions;
  }

  // ── Posted-by strip — poster (job-poster user) ──────────────────────
  //
  // Mirrors the candidate slider's submitter strip. The job's `user`
  // sub-object carries the poster's fullname + userType; the current
  // employer is found by scanning `user.workExperience` for the entry
  // flagged `currentOrganization`. Falls back to the flat scalars on
  // `job` while the user sub-object is still loading.

  private readonly posterUserTypeMap: { [key: string]: string } = {
    'FREELANCE_RECRUITER': 'Freelance Recruiter',
    'RECRUITER': 'Recruiter',
    'JOB_SEEKER': 'Job Seeker',
    'MANAGEMENT_TALENT_ACQUISITION': 'Manager',
    'MANAGEMENT TALENT ACQUISITION': 'Manager',
    'BENCH_RECRUITER': 'Bench Recruiter',
  };

  get posterName(): string {
    const j: any = this.job || {};
    return (j.user?.fullname || j.postedByUserName || '').toString().trim();
  }

  /** Job owner's (poster's) profile photo for the "Posted by" strip.
   *  Reads `job.user.photo` (the poster's user photo, NOT the business /
   *  client logo shown in the header) with the same URL normalization as
   *  `jobLogo` above, so the strip shows the photo instead of always
   *  falling back to initials.
   *
   *  Falls back to `jobSummary.user.photo` because `job` is a *shallow*
   *  merge where `fullJob` wins on key overlap (see the `job` getter): if
   *  findJobById returns a `user` sub-object without `photo`, it replaces
   *  the list row's `user` wholesale and the photo would vanish even
   *  though the list row that opened the slider had it. */
  get posterPhoto(): string {
    const photo = this.job?.user?.photo || this.jobSummary?.user?.photo;
    if (!photo) return '';
    return photo.startsWith('http') ? photo : (this.url + 'download/' + photo);
  }

  /** True when the slider was opened from Job Center AND the viewer is
   *  the job's own poster (jobPostedBy === logged-in userId). Job Center
   *  tags itself via pageFrom='job-center'. Drives two Job-Center-only
   *  tweaks: hide the Connect / personal-chat actions (you can't connect
   *  or chat with yourself) and show "You" instead of your own name on
   *  the "Posted by" strip. */
  get isOwnJobCenterJob(): boolean {
    const jobPostedBy = this.job?.jobPostedBy;
    return this.pageFrom === 'job-center'
      && !!jobPostedBy
      && jobPostedBy === this.viewerId;
  }

  get posterRole(): string {
    const j: any = this.job || {};
    const raw = (j.user?.userType || j.jobPostedByUserType || '').toString().trim();
    if (!raw) return '';
    return this.posterUserTypeMap[raw]
      || raw.replace(/_/g, ' ').replace(/\b\w/g, (ch: string) => ch.toUpperCase());
  }

  get posterOrganisation(): string {
    const j: any = this.job || {};
    const work = j.user?.workExperience;
    if (Array.isArray(work)) {
      const current = work.find((w: any) => w?.currentOrganization === true);
      if (current?.organisationName) {
        return current.organisationName.toString().trim();
      }
    }
    const org = j.jobPostedBehalfOf || j.clientName || '';
    return org && org !== 'null' ? org.toString().trim() : '';
  }

  // ── Rate formatting helpers ──────────────────────────────────────────
  // Mirror `FormValidation.getCurrency` / `getPayType` + the listing's
  // candidates-page rendering: "$ 55,000 USD / Month".

  private getCurrencyForCountry(country: string): string {
    switch (country) {
      case 'US': return 'USD';
      case 'IN': return 'INR';
      case 'CA': return 'CAD';
      case 'AU': return 'AUD';
      default:   return 'CUR';
    }
  }

  // CEIPAL jobs carry their own currency from the ATS; only USD/INR/CAD/AUD
  // are supported in the UI, anything else displays as CUR.
  private getJobCurrencyLabel(j: any): string {
    if (j?.source === 'CEIPAL') {
      const cur = (j?.currency ?? '').toString().trim().toUpperCase();
      return ['USD', 'INR', 'CAD', 'AUD'].indexOf(cur) !== -1 ? cur : 'CUR';
    }
    return this.getCurrencyForCountry(j?.country);
  }

  private getPayPeriod(payType: string): string {
    switch (payType) {
      case 'Hourly':  return 'Hr';
      case 'Daily':   return 'Day';
      case 'Weekly':  return 'Week';
      case 'Monthly': return 'Month';
      case 'Yearly':  return 'Year';
      default:        return '';
    }
  }

  private formatRateAmount(amount: any): string {
    if (amount == null || amount === '') return '';
    const n = Number(amount);
    if (!isFinite(n)) return amount.toString();
    return n.toLocaleString('en-US');
  }

  /** "55,000 USD / Month" — amount + currency + period. No "$" prefix
   *  here because the card already renders a `$` glyph before the
   *  "Offered Rate:" label. */
  private buildRateString(amount: any, payType: string, currency: string): string {
    const formatted = this.formatRateAmount(amount);
    if (!formatted) return '';
    const period = this.getPayPeriod(payType);
    return `${formatted} ${currency}${period ? ' / ' + period : ''}`.trim();
  }

  /** "X,XXX – Y,YYY USD / Month" — range variant. */
  private buildRateRangeString(lo: any, hi: any, payType: string, currency: string): string {
    const loFmt = this.formatRateAmount(lo);
    const hiFmt = this.formatRateAmount(hi);
    if (!loFmt || !hiFmt) return '';
    const period = this.getPayPeriod(payType);
    return `${loFmt} – ${hiFmt} ${currency}${period ? ' / ' + period : ''}`.trim();
  }

  /** Job's offered rate — single value or range, formatted with currency
   *  + thousands commas to match the candidate slider's "55,000 USD /
   *  Month" pattern. */
  get payRateDisplay(): string {
    const j = this.job;
    if (!j) return '';
    const lo = j.targetRateFrom;
    const hi = j.targetRateTo;
    if (lo != null && hi != null) {
      return this.buildRateRangeString(lo, hi, j.payType, this.getJobCurrencyLabel(j));
    }
    if (j.targetRate != null) {
      return this.buildRateString(j.targetRate, j.payType, this.getJobCurrencyLabel(j));
    }
    return '';
  }

  /** "60 CAD / Hr" — the rate this candidate was submitted at for THIS job.
   *  The per-job submission lives in `candidateData.jobsApplied[]`, each entry
   *  keyed by `userId === jobId` (odd naming — canonical at
   *  candidate-card.ts:1751-1752). The invite-accept flow writes here (not to
   *  the job's `appliedBy[]`, which only lists other applicants), and the
   *  updateInterestShownStatus response refreshes this array so the rate shows
   *  immediately after applying. Empty when opened without candidate context. */
  get submittedForDisplay(): string {
    const c = this.candidateData;
    const jobId = this.job?.jobId || this.jobSummary?.jobId;
    const candidateId = this.candidateId || c?.candidateId;
    const country = c?.country ?? this.job?.country ?? this.jobSummary?.country;

    // Source 1 — candidate's jobsApplied[], keyed by jobId. Populated in the
    // Job Invites flow (and patched fresh from the apply response).
    const jobsApplied: any[] = Array.isArray(c?.jobsApplied) ? c.jobsApplied : [];
    const jaMatch = jobId ? jobsApplied.find((j: any) => j?.userId === jobId || j?.jobId === jobId) : null;
    if (jaMatch) return this.buildRateString(jaMatch.targetRate, jaMatch.payType, this.getCurrencyForCountry(country));

    // Source 2 — the applied job's own appliedBy[], keyed by candidateId, which
    // the Applied tab embeds on each row. Use `jobSummary` (the applied item)
    // rather than the merged `job`, since the HTTP-cached fullJob fetch can
    // override appliedBy with a copy that lacks this candidate.
    const appliedBy: any[] = Array.isArray(this.jobSummary?.appliedBy) ? this.jobSummary.appliedBy : [];
    const abMatch = candidateId ? appliedBy.find((a: any) => a?.userId === candidateId) : null;
    if (abMatch) return this.buildRateString(abMatch.targetRate, abMatch.payType, this.getCurrencyForCountry(country));

    return '';
  }
  get primarySkills(): string[] {
    const v = this.job?.primarySkills;
    return Array.isArray(v) ? v : (typeof v === 'string' ? v.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
  }
  get secondarySkills(): string[] {
    const v = this.job?.secondarySkills;
    return Array.isArray(v) ? v : (typeof v === 'string' ? v.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
  }
  get experienceRange(): string {
    const from = this.job?.experienceFrom;
    const to = this.job?.experienceTo;
    if (from != null && to != null) return `${from}–${to} years`;
    if (from != null) return `${from}+ years`;
    return '';
  }

  trackByIndex(i: number): number { return i; }
}
